/**
 * Annotation save/load
 */
import { apiCall, getServerUrl } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { RENDERING_ENGINE_ID } from '../cornerstone/init';
import { getRenderingEngine, metaData } from '@cornerstonejs/core';
import * as cornerstoneTools from '@cornerstonejs/tools';

const { annotation } = cornerstoneTools;

const WADOURI_PATTERN = /wadouri:https?:\/\/[^"'\s]*?seriesId=(\d+)&imageIndex=(\d+)&acctoken=[^"'\s]*/g;

function stripUrls(jsonStr: string): string {
  return jsonStr.replace(WADOURI_PATTERN, (_, seriesId, imageIndex) => {
    return `ref:${seriesId}:${imageIndex}`;
  });
}

function restoreUrls(jsonStr: string): string {
  const acctoken = useAuthStore.getState().acctoken || '';
  const server = getServerUrl();
  return jsonStr.replace(/ref:(\d+):(\d+)/g, (_, seriesId, imageIndex) => {
    return `wadouri:${server}/atlrestapi/v2/dicom?seriesId=${seriesId}&imageIndex=${imageIndex}&acctoken=${acctoken}`;
  });
}

/** Get the Cornerstone-enabled element (the inner div, not our outer wrapper) */
function getEnabledElement(): HTMLDivElement | null {
  const engine = getRenderingEngine(RENDERING_ENGINE_ID);
  const vp = engine?.getViewport('viewport-0') as any;
  return vp?.element ?? null;
}

export async function saveAnnotations(studyId: number, seriesId: number): Promise<boolean> {
  const manager = annotation.state.getAnnotationManager() as any;
  const rawState = manager.annotations;

  if (!rawState || Object.keys(rawState).length === 0) return false;

  const rawJson = JSON.stringify(rawState);
  const portableJson = stripUrls(rawJson);

  try {
    const res = await apiCall('annotations.save', {
      annotationId: 0, studyId, seriesId, tool: 'ALL', data: portableJson,
    });
    return res.s === 'ok';
  } catch { return false; }
}

export async function loadAnnotations(studyId: number, seriesId: number): Promise<number> {
  try {
    const res = await apiCall<any>('annotations.list', { studyId, seriesId });
    if (res.s !== 'ok' || !res.data || res.data.length === 0) return 0;

    const enabledEl = getEnabledElement();
    if (!enabledEl) { console.warn('No enabled element for annotation load'); return 0; }

    let loaded = 0;

    for (const saved of res.data) {
      try {
        const restoredJson = restoreUrls(saved.data);
        const state = JSON.parse(restoredJson);

        // Extract all annotations from saved state
        const allAnns: any[] = [];
        if (typeof state === 'object' && !Array.isArray(state)) {
          for (const [, toolMap] of Object.entries(state)) {
            if (typeof toolMap !== 'object' || !toolMap) continue;
            for (const [, annList] of Object.entries(toolMap as any)) {
              if (Array.isArray(annList)) allAnns.push(...annList);
            }
          }
        } else if (Array.isArray(state)) {
          allAnns.push(...state);
        }

        for (const ann of allAnns) {
          try {
            ann.annotationUID = crypto.randomUUID?.() ||
              Math.random().toString(36).slice(2) + Date.now().toString(36);
            ann.isSelected = false;
            ann.highlighted = false;

            // Pass the actual Cornerstone-enabled element (not our wrapper div)
            // This ensures correct group key and triggers proper events
            annotation.state.addAnnotation(ann, enabledEl);
            loaded++;
          } catch (err) {
            console.error('Failed to add annotation:', err);
          }
        }
      } catch (err) {
        console.error('Failed to parse annotation data:', err);
      }
    }

    if (loaded > 0) {
      await new Promise((r) => setTimeout(r, 100));

      // Trigger annotation render using the enabled element
      try {
        (cornerstoneTools.utilities as any).triggerAnnotationRender(enabledEl);
      } catch (err) {
        console.error('triggerAnnotationRender failed:', err);
      }
    }

    console.log(`Annotation load — restored ${loaded} annotations`);
    return loaded;
  } catch { return 0; }
}

export async function deleteAnnotation(annotationId: number): Promise<boolean> {
  const res = await apiCall('annotations.delete', { annotationId });
  return res.s === 'ok';
}
